import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { blogApi } from '@/lib/api/blog';
import type { BlogFilters, CreateBlogPostDto, UpdateBlogPostDto, CreateCommentDto } from '@/types/blog';

// Query keys
const blogKeys = {
  all: ['blog'] as const,
  posts: (filters: BlogFilters) => [...blogKeys.all, 'posts', filters] as const,
  post: (slug: string) => [...blogKeys.all, 'post', slug] as const,
  myPosts: (filters: BlogFilters) => [...blogKeys.all, 'my-posts', filters] as const,
  comments: (postId: string) => [...blogKeys.all, 'comments', postId] as const,
  rating: (postId: string) => [...blogKeys.all, 'rating', postId] as const,
  categories: () => [...blogKeys.all, 'categories'] as const,
};

// Post queries
export function useBlogPosts(filters: BlogFilters = {}) {
  return useQuery({
    queryKey: blogKeys.posts(filters),
    queryFn: () => blogApi.getPosts(filters),
  });
}

export function useBlogPost(slug: string) {
  return useQuery({
    queryKey: blogKeys.post(slug),
    queryFn: () => blogApi.getPost(slug),
    enabled: !!slug,
  });
}

export function useMyBlogPosts(filters: BlogFilters = {}) {
  return useQuery({
    queryKey: blogKeys.myPosts(filters),
    queryFn: () => blogApi.getMyPosts(filters),
  });
}

// Post mutations
export function useCreateBlogPost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateBlogPostDto) => blogApi.createPost(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: blogKeys.all });
    },
  });
}

export function useUpdateBlogPost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBlogPostDto }) => blogApi.updatePost(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: blogKeys.all });
    },
  });
}

export function useDeleteBlogPost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => blogApi.deletePost(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: blogKeys.all });
    },
  });
}

// Comment queries & mutations
export function useBlogComments(postId: string) {
  return useQuery({
    queryKey: blogKeys.comments(postId),
    queryFn: () => blogApi.getComments(postId),
    enabled: !!postId,
  });
}

export function useCreateComment(postId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCommentDto) => blogApi.createComment(postId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: blogKeys.comments(postId) });
      queryClient.invalidateQueries({ queryKey: blogKeys.all });
    },
  });
}

export function useDeleteComment(postId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) => blogApi.deleteComment(postId, commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: blogKeys.comments(postId) });
    },
  });
}

// Rating
export function useUserRating(postId: string) {
  return useQuery({
    queryKey: blogKeys.rating(postId),
    queryFn: () => blogApi.getUserRating(postId),
    enabled: !!postId,
    retry: false,
  });
}

export function useRatePost(postId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (rating: number) => blogApi.ratePost(postId, rating),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: blogKeys.rating(postId) });
      queryClient.invalidateQueries({ queryKey: blogKeys.all });
    },
  });
}

// Categories
export function useBlogCategories() {
  return useQuery({
    queryKey: blogKeys.categories(),
    queryFn: () => blogApi.getCategories(),
  });
}

// Image upload
export function useUploadImage() {
  return useMutation({
    mutationFn: (file: File) => blogApi.uploadImage(file),
  });
}
